import { storage } from "@/lib/firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

const MAX_IMAGE_WIDTH = 1200;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_VIDEO_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const ALLOWED_VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/webm"];

export function validateMediaFile(file: File): string | null {
  const isImage = file.type.startsWith("image/");
  const isVideo = file.type.startsWith("video/");

  if (!isImage && !isVideo) {
    return "Only photos and videos are allowed.";
  }

  if (isImage && !ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return "Unsupported image format. Use JPEG, PNG, or WebP.";
  }

  if (isVideo && !ALLOWED_VIDEO_TYPES.includes(file.type)) {
    return "Unsupported video format. Use MP4 or WebM.";
  }

  if (isImage && file.size > MAX_FILE_SIZE) {
    return "Image must be under 10MB.";
  }

  if (isVideo && file.size > MAX_VIDEO_SIZE) {
    return "Video must be under 10MB.";
  }

  return null;
}

function compressImage(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const canvas = document.createElement("canvas");
      let { width, height } = img;

      if (width > MAX_IMAGE_WIDTH) {
        height = (height * MAX_IMAGE_WIDTH) / width;
        width = MAX_IMAGE_WIDTH;
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) return reject(new Error("Canvas context failed"));

      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject(new Error("Compression failed"));
        },
        "image/jpeg",
        0.8,
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Failed to load image"));
    };
    img.src = objectUrl;
  });
}

export async function uploadReportMedia(
  userId: string,
  reportId: string,
  file: File,
): Promise<{ url: string; mediaType: "photo" | "video" }> {
  // Validate before uploading
  const validationError = validateMediaFile(file);
  if (validationError) throw new Error(validationError);

  const isVideo = file.type.startsWith("video/");
  const mediaType = isVideo ? "video" : "photo";

  let uploadData: Blob | File = file;
  if (!isVideo) {
    uploadData = await compressImage(file);
  }

  const ext = isVideo ? (file.type === "video/webm" ? "webm" : "mp4") : "jpg";
  const contentType = isVideo ? file.type : "image/jpeg";
  const storageRef = ref(storage, `reports/${userId}/${reportId}/media.${ext}`);
  await uploadBytes(storageRef, uploadData, { contentType });

  const url = await getDownloadURL(storageRef);
  return { url, mediaType };
}
